/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Col, Row, Spin, notification } from "antd";
import axios from "axios";
import Icon from "@ant-design/icons";
import {
  ToteIcon,
  TrolleyIcon,
  PriorityIcon,
  CustomSpin,
  LeftIcon,
} from "../../assets/icons/icons";
import Header from "../../components/layout/header";
import UniversalScanButton from "../../components/mobile/UniversalScanButton";
import TrolleyConfirmationModal from "../../components/mobile/TrolleyConfirmationModal";
import LocationConfirmationModal from "../../components/mobile/LocationConfirmationModal";
import UnloadToteConfirmationModal from "../../components/mobile/UnloadToteConfirmationModal";
import SuccessAlert from "../../components/mobile/SuccessAlert";
import { api } from "../../services/api";
import { useToteById, useLocations } from "../common/queries";
import {
  useLoadToteToTrolley,
  useUpdateToteLocation,
  useUnloadToteFromTrolley,
} from "../common/mutations";
import Styles from "./toteInfo.module.scss";
import { getTrolleyStatus } from "../../components/reusable/styles";
import { convertUTCToLocal } from "../../utils/timeUtils";
import { isAirlogEmptyHoldingLocation } from "../../utils/locationUtils";

const ToteInfo = () => {
  const { id: encodedId } = useParams<{ id: string }>();
  // Decode the ID to handle special characters (for consistency)
  const id = encodedId ? decodeURIComponent(encodedId) : undefined;
  const navigate = useNavigate();

  // React Query hooks
  const {
    data: toteData,
    isLoading,
    error,
    refetch: refetchTote,
  } = useToteById(id);
  const loadToteMutation = useLoadToteToTrolley();
  const updateLocationMutation = useUpdateToteLocation();
  const unloadToteMutation = useUnloadToteFromTrolley();
  const { data: locationsData } = useLocations();

  const [scanType, setScanType] = useState<"trolley" | "location" | null>(null);
  const [isTrolleyModalOpen, setIsTrolleyModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isUnloadModalOpen, setIsUnloadModalOpen] = useState(false);
  const [scannedCode, setScannedCode] = useState("");
  const [isTrolleySuccessOpen, setIsTrolleySuccessOpen] = useState(false);
  const [isLocationSuccessOpen, setIsLocationSuccessOpen] = useState(false);
  const [isUnloadSuccessOpen, setIsUnloadSuccessOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    primary: string;
    secondary?: string | null;
    barcode: string;
    status?: "In" | "Out" | null;
  } | null>(null);
  const [scanErrorMessage, setScanErrorMessage] = useState<string | null>(null);
  const scanErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [locationSuccessTitle, setLocationSuccessTitle] = useState<string>(
    "Tote Location Updated"
  );
  const [locationSuccessMessage, setLocationSuccessMessage] = useState<any>("");
  const [trolleySuccessMessage, setTrolleySuccessMessage] =
    useState<React.ReactNode>("");
  const [isCheckingTrolleyId, setIsCheckingTrolleyId] = useState(false);
  const PENDING_LOAD_STORAGE_KEY = "pendingTrolleyLoadContext";
  const [unloadSuccessMessage, setUnloadSuccessMessage] = useState<string>(
    "Tote successfully unloaded from Trolley"
  );
  const [secondaryLocationActions, setSecondaryLocationActions] = useState<
    Map<string, "In" | "Out">
  >(new Map());

  const locationLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        primary: string;
        secondary?: string | null;
        barcode: string;
        status?: "In" | "Out" | null;
      }
    >();
    locationsData?.locations?.forEach((location) => {
      if (location.location_barcode) {
        map.set(location.location_barcode.trim().toLowerCase(), {
          primary: location.primary_location,
          secondary: location.secondary_location,
          barcode: location.location_barcode,
          status: location.current_location_type ?? null,
        });
      }
    });
    return map;
  }, [locationsData?.locations]);

  const showScanError = useCallback((message: string) => {
    setScanErrorMessage(message);
    if (scanErrorTimerRef.current) {
      clearTimeout(scanErrorTimerRef.current);
    }
    scanErrorTimerRef.current = setTimeout(() => {
      setScanErrorMessage(null);
    }, 5000);
  }, []);

  const formatLocationForMessage = useCallback(
    (
      locationDetails: {
        primary: string;
        secondary?: string | null;
        barcode: string;
        status?: "In" | "Out" | null;
      } | null,
      type: "In" | "Out"
    ) => {
      if (!locationDetails) {
        return `the selected location (${type})`;
      }
      const parts = [`${locationDetails.primary} (${type})`];
      if (locationDetails.secondary) {
        parts.push(locationDetails.secondary);
      }
      return parts.join(", ");
    },
    []
  );

  useEffect(() => {
    return () => {
      if (scanErrorTimerRef.current) {
        clearTimeout(scanErrorTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (error && !isLoading && id) {
      notification.info({
        message: "Tote Not Found",
        description: `Tote ID: ${id} is not registered in our system`,
      });
      // Navigate to register flow when tote is not found
      const timer = setTimeout(() => {
        navigate(`/register-tote-error?toteId=${encodeURIComponent(id)}`);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [error, isLoading, navigate, id]);

  const canLoadToTrolley =
    (toteData?.activityStatus?.toLowerCase() === "available" ||
      toteData?.activityStatus?.toLowerCase() === "unloaded from trolley") &&
    !toteData?.isLoadedToTrolley;

  const persistPendingLoadContext = useCallback(
    (payload: { toteId: string; trolleyId: string }) => {
      sessionStorage.setItem(PENDING_LOAD_STORAGE_KEY, JSON.stringify(payload));
    },
    []
  );

  const clearPendingLoadContext = useCallback(() => {
    sessionStorage.removeItem(PENDING_LOAD_STORAGE_KEY);
  }, []);

  const handleTrolleyScanned = async (code: string) => {
    if (!toteData) {
      return true;
    }

    if (!canLoadToTrolley) {
      notification.warning({
        message: "Tote Not Available",
        description: "This tote is not available for loading.",
        duration: 4,
      });
      return true;
    }

    const trimmed = code.trim();
    if (!trimmed) {
      notification.error({
        message: "Invalid Trolley ID",
        description: "Please scan a valid trolley ID.",
      });
      return false;
    }

    setIsCheckingTrolleyId(true);
    try {
      const trolleyData = await api.getTrolleyById(trimmed);
      
      // Check if trolley is in a restricted state
      const trolleyStatus = trolleyData?.status?.toLowerCase().trim() || "";
      
      // Restricted states that prevent loading totes:
      // - Unloading/UNLOADING
      // - Empty - Not Available
      // - Empty (standalone status, not "Empty - Available")
      // - Unserviceable - Awaiting Repair
      // - Unserviceable
      const isRestricted =
        trolleyStatus === "unloading" ||
        trolleyStatus === "empty - not available" ||
        trolleyStatus === "empty" ||
        trolleyStatus === "unserviceable - awaiting repair" ||
        trolleyStatus === "unserviceable";

      if (isRestricted) {
        notification.error({
          message: "Loading Restricted",
          description: "Loading restricted. The Trolley is in a state that does not allow new Totes.",
          duration: 5,
        });
        return true; // Close scanner and stay on page
      }

      clearPendingLoadContext();
      setScannedCode(trimmed);
      setScanType("trolley");
      setIsTrolleyModalOpen(true);
      return true;
    } catch (error) {
      const message =
        axios.isAxiosError(error) &&
        (error.response?.data?.detail || error.response?.data?.message);

      notification.info({
        message: "Trolley Not Registered",
        description:
          message ||
          `Trolley (ID: ${trimmed}) isn't registered in our system. Redirecting to register flow.`,
        duration: 4,
      });

      if (toteData?.id) {
        persistPendingLoadContext({ toteId: toteData.id, trolleyId: trimmed });
      }

      setTimeout(() => {
        navigate(
          `/register-trolley-error?trolleyId=${encodeURIComponent(trimmed)}`
        );
      }, 500);

      return true;
    } finally {
      setIsCheckingTrolleyId(false);
    }
  };

  const handleLocationScanned = async (code: string): Promise<boolean> => {
    const trimmed = code.trim();
    if (!trimmed) {
      notification.info({
        message: "Location Not Found",
        description: `Scanned location "${trimmed}" is not available in the system`,
      });
      return false;
    }

    const matchedLocation = locationLookup.get(trimmed.toLowerCase());

    if (!matchedLocation) {
      notification.info({
        message: "Location Not Found",
        description: `Scanned location "${trimmed}" is not available in the system`,
      });
      return false;
    }

    setSelectedLocation(matchedLocation);
    setScannedCode(trimmed);
    setScanType("location");
    setScanErrorMessage(null);
    setIsLocationModalOpen(true);
    return true;
  };

  const handleTrolleyConfirm = async () => {
    if (!toteData || !scannedCode) return;

    try {
      const response = await loadToteMutation.mutateAsync({
        toteId: toteData.id,
        trolleyId: scannedCode,
        toteRecordId: toteData.recordId,
      });

      clearPendingLoadContext();
      setIsTrolleyModalOpen(false);
      setScanType(null);
      setTrolleySuccessMessage(
        response?.message || (
          <>
            Tote successfully assigned to Trolley (ID: <b style={{ color: "#101010" }}>{scannedCode}</b>)
          </>
        )
      );
      setIsTrolleySuccessOpen(true);
      await refetchTote();
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message
        : undefined;
      notification.error({
        message: "Failed to assign the Tote to the Trolley! Try again later.",
        description,
        duration: 5,
      });
    }
  };

  const handleTrolleyCancel = () => {
    setIsTrolleyModalOpen(false);
    setScannedCode("");
    setScanType(null);
  };

  const handleLocationConfirm = async (selectedLocationType?: "In" | "Out") => {
    if (!toteData || !scannedCode) return;

    const finalLocationType = selectedLocationType || "In";

    // Check if this is a secondary location and if action was already performed
    if (selectedLocation?.secondary) {
      const secondaryKey = `${selectedLocation.primary.toLowerCase()}|${selectedLocation.secondary.toLowerCase()}`;
      const performedAction = secondaryLocationActions.get(secondaryKey);
      
      if (performedAction === finalLocationType) {
        notification.error({
          message: "Action Already Performed",
          description: "Action already performed for this location.",
          duration: 3,
        });
        return;
      }
    }

    // Validation: Prevent tote from moving INTO or OUT of Airlog Empty Holding Area
    if (selectedLocation?.primary && isAirlogEmptyHoldingLocation(selectedLocation.primary)) {
      notification.error({
        message: "Action Not Allowed",
        description: "A Tote cannot be moved into or out of the Airlog Empty Holding Area.",
        duration: 5,
      });
      return;
    }

    try {
      await updateLocationMutation.mutateAsync({
        toteId: toteData.id,
        locationBarcode: scannedCode,
        locationType: finalLocationType,
        toteRecordId: toteData.recordId,
      });

      // Track the action for secondary locations
      if (selectedLocation?.secondary) {
        const secondaryKey = `${selectedLocation.primary.toLowerCase()}|${selectedLocation.secondary.toLowerCase()}`;
        setSecondaryLocationActions((prev) => {
          const newMap = new Map(prev);
          newMap.set(secondaryKey, finalLocationType);
          return newMap;
        });
      }

      setIsLocationModalOpen(false);
      const formattedLocation = formatLocationForMessage(
        selectedLocation,
        finalLocationType
      );
      if (finalLocationType === "In") {
        setLocationSuccessTitle("Tote Location Updated");

        setLocationSuccessMessage(
          <>
            Tote is successfully assigned to
            <div
              style={{
                color: "#101010",
                fontWeight: 600,
                marginTop: 10,
              }}
            >
              {formattedLocation}
            </div>
          </>
        );
      } else {
        setLocationSuccessTitle("Tote Dispatched Successfully");

        setLocationSuccessMessage(
          <>
            Tote dispatched from
            <div
              style={{
                color: "#101010",
                fontWeight: 600,
                marginTop: 10,
              }}
            >
              {formattedLocation}
            </div>
          </>
        );
      }

      setIsLocationSuccessOpen(true);
      setSelectedLocation(null);
      setScanType(null);
      setScannedCode("");
      void refetchTote();
    } catch (error) {
      console.error("Failed to update tote location", error);
      const errorDescription = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message || error.message
        : error instanceof Error
        ? error.message
        : "Failed to update tote location. Please try again.";
      
      // Check if error is related to invalid location
      const errorLower = errorDescription.toLowerCase();
      const isLocationError = 
        errorLower.includes("location") && 
        (errorLower.includes("not found") || 
         errorLower.includes("invalid") || 
         errorLower.includes("not available") ||
         errorLower.includes("does not exist"));
      
      if (isLocationError) {
        notification.info({
          message: "Location Not Found",
          description: `Scanned location "${scannedCode}" is not available in the system`,
        });
      } else {
        notification.error({
          message: "Update Failed",
          description: errorDescription,
          duration: 5,
        });
      }
      
      // Close the modal on error
      setIsLocationModalOpen(false);
      setSelectedLocation(null);
      setScanType(null);
      setScannedCode("");
      setScanErrorMessage(null);
    }
  };

  const handleLocationCancel = () => {
    setIsLocationModalOpen(false);
    setScannedCode("");
    setSelectedLocation(null);
    setScanErrorMessage(null);
    setScanType(null);
  };

  const handleUnloadClick = () => {
    setIsUnloadModalOpen(true);
  };

  const handleUnloadConfirm = async () => {
    if (!toteData) return;

    try {
      const response = await unloadToteMutation.mutateAsync({
        toteId: toteData.id,
        trolleyId: toteData.trolleyId,
        toteRecordId: toteData.recordId,
      });

      setUnloadSuccessMessage(
        response?.message || "Tote successfully unloaded from Trolley"
      );
      setIsUnloadModalOpen(false);
      setIsUnloadSuccessOpen(true);
      await refetchTote();
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? error.response?.data?.detail || error.response?.data?.message
        : undefined;
      notification.error({
        message: "Failed to unload the Tote from the Trolley! Try again later.",
        description,
        duration: 5,
      });
    }
  };

  const handleUnloadCancel = () => {
    setIsUnloadModalOpen(false);
  };

  useEffect(() => {
    if (!toteData?.id || isLoading) {
      return;
    }

    try {
      const stored = sessionStorage.getItem(PENDING_LOAD_STORAGE_KEY);
      if (!stored) {
        return;
      }
      const parsed = JSON.parse(stored) as {
        toteId: string;
        trolleyId: string;
      } | null;

      if (parsed && parsed.toteId === toteData.id) {
        setScannedCode(parsed.trolleyId);
        setScanType("trolley");
        setIsTrolleyModalOpen(true);
        clearPendingLoadContext();
      }
    } catch (err) {
      console.warn("Failed to parse pending load context", err);
      clearPendingLoadContext();
    }
  }, [toteData?.id, isLoading, clearPendingLoadContext]);

  return (
    <div className={Styles.mainContainer}>
      <Header />

      <div className={Styles.contentContainer}>
        <Spin spinning={isLoading} indicator={<CustomSpin />}>
          <Row
            align="middle"
            justify="space-between"
            style={{ marginBottom: "20px" }}
          >
            <Icon
              component={LeftIcon}
              onClick={() => navigate("/operator-landing")}
            />
            <div className={Styles.title}>Tote Information</div>
            <div />
          </Row>

          <div className={Styles.infoSection}>
            <h3 className={Styles.sectionTitle}>Tote Info</h3>
            <Card className={Styles.infoCard}>
              <div className={Styles.toteHeader}>
                <div className={Styles.toteInfoRow}>
                  <Icon component={ToteIcon} className={Styles.icon} />
                  <div>
                    <div className="labels">Tote No</div>
                    <div className="values">{toteData?.toteNo}</div>
                  </div>
                </div>
                {toteData?.priority && (
                  <div className="priorityTag">
                    <Icon component={PriorityIcon} />
                    <span>Priority</span>
                  </div>
                )}
              </div>

              <Row gutter={[16, 16]} className={Styles.detailsGrid}>
                {/* <Col span={12}>
                  <div className="labels">Weight</div>
                  <div className={Styles.detailValue}>{toteData?.weight}</div>
                </Col>
                <Col span={12}>
                  <div className="labels">Item count</div>
                  <div className={Styles.detailValue}>
                    {toteData?.itemCount}
                  </div>
                </Col> */}
                <Col span={12}>
                  <div className="labels">MSN</div>
                  <div className={Styles.detailValue}>{toteData?.msn}</div>
                </Col>
                <Col span={12}>
                  <div className="labels">USM</div>
                  <div className={Styles.detailValue}>{toteData?.usn || toteData?.usm}</div>
                </Col>

                <Col span={24}>
                  <div className="labels">Last Scanned Location</div>
                  <div className={Styles.detailValue}>
                    {toteData?.lastScannedLocation || "N/A"}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="labels">Status</div>
                  <div
                    style={{
                      color: getTrolleyStatus(toteData?.activityStatus),
                    }}
                    className={Styles.status}
                  >
                    {toteData?.activityStatus || "N/A"}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="labels">Registered Date</div>
                  <div className={Styles.detailValue}>
                    {toteData?.registeredDate
                      ? convertUTCToLocal(toteData?.registeredDate)
                      : "N/A"}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="labels">Point of Delivery</div>
                  <div className={Styles.detailValue}>
                    {toteData?.pointOfDelivery || "N/A"}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="labels">Date of Delivery</div>
                  <div className={Styles.detailValue}>
                    {toteData?.dateOfDelivery
                      ? convertUTCToLocal(toteData.dateOfDelivery)
                      : "N/A"}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="labels">Point of Use / Station</div>
                  <div className={Styles.detailValue}>
                    {toteData?.pointOfUse || "N/A"}
                  </div>
                </Col>
                <Col span={12}>
                  <div className="labels">Date of Use</div>
                  <div className={Styles.detailValue}>
                    {toteData?.dateOfUse
                      ? convertUTCToLocal(toteData.dateOfUse)
                      : "N/A"}
                  </div>
                </Col>
              </Row>
            </Card>
          </div>

          <div className={Styles.infoSection}>
            <h3 className={Styles.sectionTitle}>Trolley Info</h3>
            <Card className={Styles.infoCard}>
              <Row gutter={15}>
                <Col className="iconBox" style={{ background: "#e6f3f7" }}>
                  <Icon component={TrolleyIcon} />
                </Col>
                <Col>
                  <div className="labels">Trolley ID</div>
                  <div className="values">
                    {toteData?.isLoadedToTrolley && toteData?.trolleyId
                      ? toteData?.trolleyId
                      : "Tote not loaded into trolley"}
                  </div>
                </Col>
              </Row>
              {toteData?.isLoadedToTrolley && toteData?.trolleyId && (
                <Row gutter={[16, 16]} className={Styles.detailsGrid}>
                  <Col span={12}>
                    <div className="labels">Status</div>
                    <div
                      className={Styles.detailValue}
                      style={{
                        color: getTrolleyStatus(toteData?.trolleyStatus),
                      }}
                    >
                      {toteData?.trolleyStatus || "N/A"}
                    </div>
                  </Col>
                  <Col span={24}>
                    <div className="labels">Last Scanned Location</div>
                    <div className={Styles.detailValue}>
                      {toteData?.trolleyLastScannedLocation || "N/A"}
                    </div>
                  </Col>
                </Row>
              )}
            </Card>
          </div>
        </Spin>
      </div>

      {!(
        toteData?.isLoadedToTrolley &&
        toteData?.activityStatus === "Loaded to Trolley"
      ) &&
      toteData?.activityStatus?.toLowerCase() !== "completed" && (
        <UniversalScanButton
          mode="tote-info"
          onTrolleyScanned={handleTrolleyScanned}
          onLocationScanned={handleLocationScanned}
          scanErrorMessage={scanErrorMessage}
          onScanErrorClear={() => setScanErrorMessage(null)}
          isCheckingTrolley={isCheckingTrolleyId}
          availableActions={
            canLoadToTrolley ? ["trolley", "location"] : ["location"]
          }
        />
      )}

      {toteData?.isLoadedToTrolley &&
        toteData?.activityStatus === "Loaded to Trolley" && (
          <div className={Styles.bottomAction}>
            <Button
              className={Styles.unloadButton}
              size="large"
              onClick={handleUnloadClick}
            >
              Unload From Trolley
            </Button>
          </div>
        )}

      {scanType === "trolley" && scannedCode && (
        <TrolleyConfirmationModal
          open={isTrolleyModalOpen}
          trolleyId={scannedCode}
          onConfirm={handleTrolleyConfirm}
          onCancel={handleTrolleyCancel}
          isLoading={loadToteMutation.isPending}
        />
      )}

      {scanType === "location" && scannedCode && (
        <LocationConfirmationModal
          open={isLocationModalOpen}
          location={selectedLocation}
          lastScannedLocation={toteData?.lastScannedLocation}
          lastLocationStatus={toteData?.currentLocationType ?? null}
          onConfirm={handleLocationConfirm}
          onCancel={handleLocationCancel}
          isLoading={updateLocationMutation.isPending}
          secondaryLocationActions={secondaryLocationActions}
        />
      )}

      {toteData?.isLoadedToTrolley && toteData?.trolleyId && (
        <UnloadToteConfirmationModal
          open={isUnloadModalOpen}
          toteId={toteData.id}
          trolleyId={toteData.trolleyId}
          onConfirm={handleUnloadConfirm}
          onCancel={handleUnloadCancel}
          isLoading={unloadToteMutation.isPending}
        />
      )}

      <SuccessAlert
        open={isTrolleySuccessOpen}
        title="Tote Loaded to Trolley"
        message={
          trolleySuccessMessage || (
            <>
              Tote successfully assigned to Trolley (ID: <b style={{ color: "#101010" }}>{scannedCode}</b>)
            </>
          )
        }
        buttonText="Go to Tote Listing"
        onClose={() => {
          setIsTrolleySuccessOpen(false);
          setScannedCode("");
          setTrolleySuccessMessage("");
          navigate("/operator-landing");
        }}
      />

      <SuccessAlert
        open={isLocationSuccessOpen}
        title={locationSuccessTitle}
        message={locationSuccessMessage}
        buttonText="Go to Tote Information"
        onClose={() => {
          setIsLocationSuccessOpen(false);
          setLocationSuccessMessage("");
          setLocationSuccessTitle("Tote Location Updated");
          void refetchTote();
        }}
      />

      <SuccessAlert
        open={isUnloadSuccessOpen}
        title="Tote Unloaded Successfully"
        message={unloadSuccessMessage}
        buttonText="Go to Tote Listing"
        onClose={() => {
          setIsUnloadSuccessOpen(false);
          setUnloadSuccessMessage("Tote successfully unloaded from Trolley");
          navigate("/operator-landing");
        }}
      />
    </div>
  );
};

export default ToteInfo;
